import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { FileSignature, Download, SlidersHorizontal, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { servicesEngagementAPI as API, LETTER_STATUS_META, fmtMoney, downloadLetterPdf } from '../../lib/servicesEngagementAPI';
import { ethicalAPI, ETHICAL_STATUS_META, downloadEthicalPdf } from '../../lib/ethicalLettersAPI';
import { ConsentPanel } from './ConsentPanel';

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU'); } catch { return iso; } };

export const EngagementLettersTab = ({ clientId, role, onClientChanged }) => {
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [ethical, setEthical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [res, eth] = await Promise.all([
          API.listForClient(clientId),
          ethicalAPI.list(clientId).catch(() => ({ data: { letters: [] } })),
        ]);
        setLetters(res.data.letters || []);
        setEthical(eth.data.letters || []);
      } catch { /* non-fatal */ }
      finally { setLoading(false); }
    })();
  }, [clientId, tick]);

  const download = async (l) => {
    try { await downloadLetterPdf(l.id, l.engagement_letter_number); }
    catch { toast.error('Failed to download PDF'); }
  };

  return (
    <div className="space-y-6">
    <ConsentPanel clientId={clientId} role={role} onSent={() => setTick((n) => n + 1)} onRestrictionChange={onClientChanged} />
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Engagement Letters</CardTitle>
        <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${clientId}/engagement`)} data-testid="manage-engagement-btn">
          <SlidersHorizontal className="w-4 h-4 mr-2" /> Manage Services &amp; Letter
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse h-32 bg-muted rounded" />
        ) : letters.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <FileSignature className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p>No engagement letters yet for this client.</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Reference</TableHead><TableHead>Template</TableHead><TableHead>Total</TableHead>
              <TableHead>Signed</TableHead><TableHead>Status</TableHead><TableHead className="text-right">PDF</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {letters.map((l) => (
                <TableRow key={l.id} data-testid={`client-letter-${l.id}`}>
                  <TableCell className="font-medium">{l.engagement_letter_number}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.template_name || '—'}</TableCell>
                  <TableCell className="text-sm">{fmtMoney(l.fee_summary?.total)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmt(l.signed_at)}</TableCell>
                  <TableCell><Badge className={(LETTER_STATUS_META[l.status] || {}).color}>{(LETTER_STATUS_META[l.status] || {}).label || l.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => download(l)} data-testid={`download-letter-${l.id}`}>
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>

    {ethical.length > 0 && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <Scale className="w-5 h-5 text-primary" /> Ethical Clearance Letters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Reference</TableHead><TableHead>Outgoing accountant</TableHead>
              <TableHead>Sent</TableHead><TableHead>Status</TableHead><TableHead className="text-right">PDF</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {ethical.map((l) => (
                <TableRow key={l.id} data-testid={`ethical-letter-${l.id}`}>
                  <TableCell className="font-medium">{l.letter_number}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.outgoing_name}{l.outgoing_firm ? ` — ${l.outgoing_firm}` : ''}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmt(l.sent_at)}</TableCell>
                  <TableCell><Badge className={(ETHICAL_STATUS_META[l.status] || {}).color}>{(ETHICAL_STATUS_META[l.status] || {}).label || l.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`download-ethical-${l.id}`}
                      onClick={() => downloadEthicalPdf(l.id, l.letter_number).catch(() => toast.error('Failed to download PDF'))}>
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )}
    </div>
  );
};
